// apps/admin/src/pages/settings/VendorConfigsTab.tsx
//
// Phase 8.3 Wave 6 Task 41 — Vendor Configs tab.
//
// vg-binding: CONTEXT:decisions=P8.D-38,P8.D-40,P8.D-100,P8.D-101,P8.D-120,P8.D-170,P8.D-466,P8.D-478
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Route: /system/settings/vendor-configs
//
// Scope (per task-41 PLAN + VendorSettingsValues strict schema):
//   - default_sla_thresholds (free JSON record of number — surfaced as
//     editable rows: key + minutes)
//   - payout_schedule (free string — preset dropdown + custom)
//   - hold_period_by_tier (hold tiers per vendor tier; rows of {tier, days})
//     Default tiers: tier1 / tier2 / tier3 = 7 / 14 / 21 days per PLAN.
//   - capacity_alerts (low / warning / critical % thresholds
//     defaults 80 / 95 / 100)
//   - at_risk_thresholds (same shape as merchant at-risk)
//   - low_stock_escalation_sla_hours (default 48, 1..168 range)
//
// Permission gates:
//   - Read: `system:settings:read`
//   - Write: `system:settings:write:vendor`

import { useCallback, useMemo, useState, type JSX } from 'react';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormLabel,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Spinner,
  useAuth,
  usePermission,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import { ConflictDiffModal } from '../../features/settings/ConflictDiffModal.js';
import { StickySaveBar } from '../../features/settings/StickySaveBar.js';
import { useSettings } from '../../features/settings/useSettings.js';
import type { VendorSettingsValues } from '../../api/systemSettings.api.js';

const PAYOUT_SCHEDULE_OPTIONS = [
  'weekly_friday',
  'biweekly',
  'monthly_first',
  'monthly_last',
  'on_demand',
] as const;

// Stable empty-roles ref — a `[]` literal inside the useAuth selector returns
// a new array each render when roles is nullish → useSyncExternalStore
// infinite loop (dbg-20260710 #2593). Coalesce against this frozen constant.
const EMPTY_ROLES: readonly unknown[] = [];

function stringifyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function VendorConfigsTab(): JSX.Element {
  const { t } = useTranslation('settings');
  const canRead = usePermission('system:settings:read');
  const canWrite = usePermission('system:settings:write:vendor');
  const userRoles = useAuth((s) => s.user?.roles) ?? EMPTY_ROLES;
  const isSuperAdmin = useMemo(
    () =>
      userRoles.some((r) =>
        typeof r === 'string'
          ? r === 'superadmin'
          : (r as { role?: string })?.role === 'superadmin',
      ),
    [userRoles],
  );

  const {
    loading,
    saving,
    envelope,
    draft,
    dirty,
    conflict,
    setDraft,
    reload,
    save,
    forceOverwrite,
    dismissConflict,
  } = useSettings<VendorSettingsValues>('vendor');

  const [slaText, setSlaText] = useState<string | null>(null);
  const [slaError, setSlaError] = useState<string | null>(null);

  const commitSla = useCallback((): void => {
    if (slaText == null) return;
    try {
      const parsed = JSON.parse(slaText) as Record<string, number>;
      setDraft((prev) => ({ ...prev, default_sla_thresholds: parsed }));
      setSlaError(null);
    } catch (err) {
      setSlaError(
        err instanceof Error ? err.message : t('vendorConfigs.slaThresholds.invalidJson'),
      );
    }
  }, [slaText, setDraft, t]);

  const handleSave = useCallback((): void => {
    commitSla();
    if (slaError) return;
    void save();
  }, [commitSla, slaError, save]);

  if (!canRead) {
    return (
      <div className="rounded-lg border border-pw-border bg-pw-surface p-6 text-sm text-pw-text-muted">
        {t('vendorConfigs.noPermission')}
      </div>
    );
  }

  if (loading || !envelope || !draft) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner label={t('vendorConfigs.loading')} />
      </div>
    );
  }

  const hold = draft.hold_period_by_tier ?? {};
  const updateHold = (tier: string, days: number): void => {
    setDraft((prev) => ({
      ...prev,
      hold_period_by_tier: { ...prev.hold_period_by_tier, [tier]: days },
    }));
  };
  const removeHoldTier = (tier: string): void => {
    setDraft((prev) => {
      const next = { ...prev.hold_period_by_tier };
      delete next[tier];
      return { ...prev, hold_period_by_tier: next };
    });
  };

  const cap = draft.capacity_alerts ?? { low: 80, warning: 95, critical: 100 };
  const updateCap = (key: keyof typeof cap, value: number): void => {
    setDraft((prev) => ({
      ...prev,
      capacity_alerts: { ...cap, [key]: value },
    }));
  };

  const risk = draft.at_risk_thresholds ?? {
    low_rating_below: 0,
    high_rejection_above: 0,
    complaint_spike_count: 0,
    complaint_window_hours: 0,
  };
  const updateRisk = (
    key: keyof VendorSettingsValues['at_risk_thresholds'],
    value: number,
  ): void => {
    setDraft((prev) => ({
      ...prev,
      at_risk_thresholds: { ...risk, [key]: value },
    }));
  };

  return (
    <div className="flex flex-col gap-6" data-testid="settings-vendor-tab">
      <div className="flex items-center justify-between text-xs text-pw-text-muted">
        <span>
          {t('vendorConfigs.versionLabel')}{' '}
          <strong className="font-semibold">v{envelope.version}</strong>
          {envelope.updated_by_name ? (
            <> · {t('vendorConfigs.lastEditedBy', { name: envelope.updated_by_name })}</>
          ) : null}
          {envelope.updated_at ? (
            <> · {new Date(envelope.updated_at).toLocaleString()}</>
          ) : null}
        </span>
        <button
          type="button"
          onClick={() => {
            void reload();
          }}
          className="text-xs font-medium text-pw-primary-strong hover:underline"
          data-testid="settings-vendor-reload-btn"
        >
          {t('vendorConfigs.reload')}
        </button>
      </div>

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <Card>
          <CardHeader>
            <CardTitle>{t('vendorConfigs.payouts.title')}</CardTitle>
            <CardDescription>
              {t('vendorConfigs.payouts.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <FormField>
              <FormLabel htmlFor="vendor-payout-schedule">
                {t('vendorConfigs.payouts.scheduleLabel')}
              </FormLabel>
              <FormControl>
                <Select
                  value={draft.payout_schedule}
                  onValueChange={(value) =>
                    setDraft((prev) => ({
                      ...prev,
                      payout_schedule: value,
                    }))
                  }
                  disabled={!canWrite}
                >
                  <SelectTrigger
                    id="vendor-payout-schedule"
                    data-testid="settings-vendor-payout-schedule"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYOUT_SCHEDULE_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {t(`vendorConfigs.payoutScheduleOptions.${opt}`)}
                      </SelectItem>
                    ))}
                    {!PAYOUT_SCHEDULE_OPTIONS.includes(
                      draft.payout_schedule as (typeof PAYOUT_SCHEDULE_OPTIONS)[number],
                    ) ? (
                      <SelectItem value={draft.payout_schedule}>
                        {draft.payout_schedule}
                      </SelectItem>
                    ) : null}
                  </SelectContent>
                </Select>
              </FormControl>
            </FormField>

            <fieldset className="rounded-md border border-pw-border p-3">
              <legend className="px-2 text-xs font-medium uppercase tracking-wide text-pw-text-muted">
                {t('vendorConfigs.holdTier.legend')}
              </legend>
              <div className="flex flex-col gap-2">
                {Object.entries(hold).length === 0 ? (
                  <p className="px-1 text-xs text-pw-text-muted">
                    {t('vendorConfigs.holdTier.empty')}
                  </p>
                ) : null}
                {Object.entries(hold).map(([tier, days]) => (
                  <div
                    key={tier}
                    className="flex items-center gap-2"
                    data-testid={`settings-vendor-hold-row-${tier}`}
                  >
                    <Input
                      value={tier}
                      readOnly
                      className="max-w-[180px]"
                    />
                    <Input
                      type="number"
                      min={0}
                      value={days}
                      onChange={(e) =>
                        updateHold(
                          tier,
                          Number.parseInt(e.target.value, 10) || 0,
                        )
                      }
                      disabled={!canWrite}
                      className="max-w-[140px]"
                    />
                    {canWrite ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeHoldTier(tier)}
                        data-testid={`settings-vendor-hold-remove-${tier}`}
                      >
                        {t('vendorConfigs.holdTier.remove')}
                      </Button>
                    ) : null}
                  </div>
                ))}
                {canWrite ? (
                  <div className="flex items-center gap-2 pt-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        setDraft((prev) => ({
                          ...prev,
                          hold_period_by_tier: {
                            ...prev.hold_period_by_tier,
                            [`tier${Object.keys(prev.hold_period_by_tier ?? {}).length + 1}`]: 7,
                          },
                        }))
                      }
                      data-testid="settings-vendor-hold-add"
                    >
                      {t('vendorConfigs.holdTier.add')}
                    </Button>
                  </div>
                ) : null}
              </div>
            </fieldset>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t('vendorConfigs.capacity.title')}</CardTitle>
            <CardDescription>
              {t('vendorConfigs.capacity.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-3">
            <FormField>
              <FormLabel htmlFor="vendor-cap-low">{t('vendorConfigs.capacity.low')}</FormLabel>
              <FormControl>
                <Input
                  id="vendor-cap-low"
                  type="number"
                  min={0}
                  max={100}
                  value={cap.low}
                  onChange={(e) =>
                    updateCap('low', Number.parseInt(e.target.value, 10) || 0)
                  }
                  disabled={!canWrite}
                  data-testid="settings-vendor-cap-low"
                />
              </FormControl>
            </FormField>
            <FormField>
              <FormLabel htmlFor="vendor-cap-warning">{t('vendorConfigs.capacity.warning')}</FormLabel>
              <FormControl>
                <Input
                  id="vendor-cap-warning"
                  type="number"
                  min={0}
                  max={100}
                  value={cap.warning}
                  onChange={(e) =>
                    updateCap(
                      'warning',
                      Number.parseInt(e.target.value, 10) || 0,
                    )
                  }
                  disabled={!canWrite}
                  data-testid="settings-vendor-cap-warning"
                />
              </FormControl>
            </FormField>
            <FormField>
              <FormLabel htmlFor="vendor-cap-critical">{t('vendorConfigs.capacity.critical')}</FormLabel>
              <FormControl>
                <Input
                  id="vendor-cap-critical"
                  type="number"
                  min={0}
                  max={100}
                  value={cap.critical}
                  onChange={(e) =>
                    updateCap(
                      'critical',
                      Number.parseInt(e.target.value, 10) || 0,
                    )
                  }
                  disabled={!canWrite}
                  data-testid="settings-vendor-cap-critical"
                />
              </FormControl>
            </FormField>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t('vendorConfigs.atRisk.title')}</CardTitle>
            <CardDescription>
              {t('vendorConfigs.atRisk.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <FormField>
              <FormLabel htmlFor="vendor-risk-low-rating">
                {t('vendorConfigs.atRisk.lowRating')}
              </FormLabel>
              <FormControl>
                <Input
                  id="vendor-risk-low-rating"
                  type="number"
                  step="0.1"
                  min={0}
                  max={5}
                  value={risk.low_rating_below}
                  onChange={(e) =>
                    updateRisk(
                      'low_rating_below',
                      Number.parseFloat(e.target.value) || 0,
                    )
                  }
                  disabled={!canWrite}
                  data-testid="settings-vendor-risk-rating"
                />
              </FormControl>
            </FormField>
            <FormField>
              <FormLabel htmlFor="vendor-risk-rejection">
                {t('vendorConfigs.atRisk.highRejection')}
              </FormLabel>
              <FormControl>
                <Input
                  id="vendor-risk-rejection"
                  type="number"
                  step="0.1"
                  min={0}
                  max={100}
                  value={risk.high_rejection_above}
                  onChange={(e) =>
                    updateRisk(
                      'high_rejection_above',
                      Number.parseFloat(e.target.value) || 0,
                    )
                  }
                  disabled={!canWrite}
                  data-testid="settings-vendor-risk-rejection"
                />
              </FormControl>
            </FormField>
            <FormField>
              <FormLabel htmlFor="vendor-risk-complaint-count">
                {t('vendorConfigs.atRisk.complaintCount')}
              </FormLabel>
              <FormControl>
                <Input
                  id="vendor-risk-complaint-count"
                  type="number"
                  min={0}
                  value={risk.complaint_spike_count}
                  onChange={(e) =>
                    updateRisk(
                      'complaint_spike_count',
                      Number.parseInt(e.target.value, 10) || 0,
                    )
                  }
                  disabled={!canWrite}
                  data-testid="settings-vendor-risk-complaint-count"
                />
              </FormControl>
            </FormField>
            <FormField>
              <FormLabel htmlFor="vendor-risk-complaint-window">
                {t('vendorConfigs.atRisk.complaintWindow')}
              </FormLabel>
              <FormControl>
                <Input
                  id="vendor-risk-complaint-window"
                  type="number"
                  min={1}
                  value={risk.complaint_window_hours}
                  onChange={(e) =>
                    updateRisk(
                      'complaint_window_hours',
                      Number.parseInt(e.target.value, 10) || 0,
                    )
                  }
                  disabled={!canWrite}
                  data-testid="settings-vendor-risk-window"
                />
              </FormControl>
            </FormField>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t('vendorConfigs.lowStock.title')}</CardTitle>
            <CardDescription>
              {t('vendorConfigs.lowStock.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormField>
              <FormLabel htmlFor="vendor-low-stock-sla">
                {t('vendorConfigs.lowStock.slaLabel')}
              </FormLabel>
              <FormControl>
                <Input
                  id="vendor-low-stock-sla"
                  type="number"
                  min={1}
                  max={168}
                  value={draft.low_stock_escalation_sla_hours}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      low_stock_escalation_sla_hours:
                        Number.parseInt(e.target.value, 10) || 1,
                    }))
                  }
                  disabled={!canWrite}
                  data-testid="settings-vendor-low-stock-sla"
                />
              </FormControl>
            </FormField>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t('vendorConfigs.slaThresholds.title')}</CardTitle>
            <CardDescription>
              {t('vendorConfigs.slaThresholds.descBefore')}{' '}
              <code className="font-mono">
                {`{ "ack_minutes": 60, "ship_minutes": 1440 }`}
              </code>
              {t('vendorConfigs.slaThresholds.descAfter')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormField>
              <FormLabel htmlFor="vendor-sla-json">
                {t('vendorConfigs.slaThresholds.jsonLabel')}
              </FormLabel>
              <FormControl>
                <textarea
                  id="vendor-sla-json"
                  value={slaText ?? stringifyJson(draft.default_sla_thresholds)}
                  onChange={(e) => {
                    setSlaText(e.target.value);
                    setSlaError(null);
                  }}
                  onBlur={commitSla}
                  rows={8}
                  spellCheck={false}
                  disabled={!canWrite}
                  className="block w-full rounded-md border border-pw-border bg-pw-bg p-3 font-mono text-xs text-pw-text focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="settings-vendor-sla-json"
                />
              </FormControl>
              {slaError ? (
                <p className="text-xs font-medium text-red-600 dark:text-red-300">
                  {t('vendorConfigs.slaThresholds.parseError', { message: slaError })}
                </p>
              ) : null}
            </FormField>
          </CardContent>
        </Card>

        <StickySaveBar
          dirty={dirty || !!slaText}
          saving={saving}
          canWrite={canWrite}
          onSave={handleSave}
          onDiscard={() => {
            setSlaText(null);
            setSlaError(null);
            void reload();
          }}
        />
      </Form>

      {conflict ? (
        <ConflictDiffModal
          open={conflict.open}
          yourValues={conflict.yourValues as unknown as Record<string, unknown>}
          serverValues={conflict.serverValues as unknown as Record<string, unknown>}
          currentVersion={conflict.currentVersion}
          expectedVersion={conflict.expectedVersion}
          isSuperAdmin={isSuperAdmin}
          onReload={() => {
            dismissConflict();
            void reload();
          }}
          onForceOverwrite={() => {
            void forceOverwrite();
          }}
          onClose={dismissConflict}
        />
      ) : null}
    </div>
  );
}

export default VendorConfigsTab;
